import React, { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import clsx from 'clsx';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// API helper
async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    const message = Array.isArray(error.message) ? error.message.join('; ') : error.message;
    const err = new Error(message || 'Request failed') as Error & { status?: number };
    err.status = response.status;
    throw err;
  }
  if (response.status === 204) return null;
  return response.json();
}

const ATTACHMENT_ACCEPT = 'image/jpeg,image/png,application/pdf';
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

// Checked again server-side against the file's bytes; this just fails fast.
function attachmentProblem(file: File | null): string | null {
  if (!file) return null;
  if (!['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(file.type)) return 'Attachment must be a JPG, PNG or PDF.';
  if (file.size > ATTACHMENT_MAX_BYTES) return 'Attachment must be 10 MB or smaller.';
  return null;
}

async function uploadAttachment(file: File, parent: { infractionId?: string; warningId?: string }) {
  const token = localStorage.getItem('admin_auth_token');
  const body = new FormData();
  body.append('file', file);
  if (parent.infractionId) body.append('infractionId', parent.infractionId);
  if (parent.warningId) body.append('warningId', parent.warningId);
  const response = await fetch(`${API_BASE}/discipline/admin/attachments`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Upload failed' }));
    throw new Error(Array.isArray(error.message) ? error.message.join('; ') : error.message || 'Upload failed');
  }
  return response.json();
}

// Files are only served to authenticated Discipline users, so they can't be a
// plain link: fetch with the token, then open the blob.
async function openAttachment(id: string) {
  const token = localStorage.getItem('admin_auth_token');
  const win = window.open('', '_blank');
  try {
    const response = await fetch(`${API_BASE}/discipline/admin/attachments/${id}/file`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error(`Couldn't open attachment (${response.status})`);
    const url = URL.createObjectURL(await response.blob());
    if (win) win.location.href = url; else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err: any) {
    win?.close();
    alert(err.message);
  }
}

// Types
interface InfractionType {
  id: string;
  name: string;
  description: string;
  category: string;
  pointsDeducted: number;
  severity: string;
  isActive: boolean;
}

interface Infraction {
  id: string;
  employeeId: string;
  employeeName: string;
  badgeNumber: string;
  infractionTypeName: string;
  category: string;
  severity: string;
  pointsDeducted: number;
  description: string;
  orderNumber: string;
  status: string;
  incidentDate: string;
  submittedByName: string;
  issuedByName: string;
  managerNotes: string;
  adminNotes: string;
  createdAt: string;
  attachmentCount?: number;
  reversedAt?: string | null;
  reversedByName?: string | null;
  reversalReason?: string | null;
  acknowledgementMethod?: string | null;
  acknowledgedAt?: string | null;
  refusedAt?: string | null;
  employeeComment?: string | null;
  disputedAt?: string | null;
  disputeReviewedAt?: string | null;
  disputeReviewedByName?: string | null;
  disputeReviewNote?: string | null;
}

interface Warning {
  id: string;
  escalationNote?: string | null;
  employeeId: string;
  employeeName: string;
  badgeNumber: string;
  warningType: string;
  reason: string;
  status: string;
  issuedByName: string;
  issuedBy: string | null;
  incidentDate: string | null;
  witnessEmployeeId: string | null;
  issuedAt: string;
  acknowledgedAt: string;
  expiresAt: string;
  suspensionDays: number;
  createdAt: string;
  infractionIds: string[] | null;
  enteredByName: string | null;
  expectationGoingForward: string | null;
  consequenceIfNotCorrected: string | null;
  escalationTrigger: 'manual' | 'threshold' | 'lifetime_max';
  suggestedWarningType: string | null;
  textWrittenForType: string | null;
  textLevelMismatch: boolean;
  ageDays: number | null;
  attachmentCount?: number;
  rescindedAt?: string | null;
  rescindedByName?: string | null;
  rescissionReason?: string | null;
  acknowledgementMethod?: string | null;
  refusedAt?: string | null;
  employeeComment?: string | null;
  disputedAt?: string | null;
  disputeReviewedAt?: string | null;
  disputeReviewedByName?: string | null;
  disputeReviewNote?: string | null;
  linkedDisputedCount?: number;
}

interface Attachment {
  id: string;
  originalFilename: string;
  mimeType: string;
  fileSize: number;
  uploadedByName: string | null;
  createdAt: string;
}

const ACK_METHODS: { value: string; label: string; hint: string }[] = [
  { value: 'not_yet_presented', label: 'Not yet presented', hint: 'Stays pending until the employee acknowledges it.' },
  { value: 'acknowledged_in_person', label: 'Acknowledged in person', hint: 'Recorded as acknowledged now.' },
  { value: 'signed_paper_on_file', label: 'Signed paper on file', hint: 'Recorded as acknowledged on the date it was signed. Attach the scan.' },
  { value: 'refused_to_sign', label: 'Refused to sign', hint: 'Recorded as refused. The warning still stands and is resolved, not left pending.' },
];
const todayIso = () => new Date().toISOString().split('T')[0];

interface PendingReviewCount {
  pendingInfractions: number;
  draftWarnings: number;
  lifetimeMaxDrafts: number;
  staleDrafts: number;
  disputes: number;
  staleDisputes: number;
  total: number;
}

interface OpenDispute {
  kind: 'infraction' | 'warning';
  id: string;
  employeeName: string;
  title: string;
  date: string | null;
  recordText: string | null;
  employeeComment: string;
  disputedAt: string;
  ageDays: number;
}

// Matches getPendingReviewCount().staleDisputes on the API (same boundary).
const STALE_DISPUTE_DAYS = 14;

// Matches getPendingReviewCount().staleDrafts on the API.
const STALE_DRAFT_DAYS = 14;

const WARNING_TYPE_LABELS: Record<string, string> = {
  verbal: 'verbal warning',
  written: 'written warning',
  final: 'final warning',
  suspension: 'suspension',
};
const warningTypeLabel = (t: string | null | undefined) => (t ? WARNING_TYPE_LABELS[t] || t : 'unknown');

// 'termination' removed 2026-09-16 — a separation is not a warning (Separation module).
const WARNING_TYPE_ORDER = ['verbal', 'written', 'final', 'suspension'];
// Mirrors discipline_warnings_written_requires_expectation. The API and the
// database CHECK enforce it too; this is here so the form says what's missing
// before submit instead of after.
const TYPES_REQUIRING_TEXT = ['written', 'final', 'suspension'];

function missingRequiredFields(f: { warningType: string; reason: string; expectation: string; consequence: string }) {
  const missing: string[] = [];
  if (TYPES_REQUIRING_TEXT.includes(f.warningType)) {
    if (!f.expectation.trim()) missing.push('Expectation going forward');
    if (!f.consequence.trim()) missing.push('Consequence if not corrected');
  }
  // A person must write the reason on every issue — drafts arrive with none.
  if (!f.reason.trim()) missing.push('Reason');
  return missing;
}

interface EmployeeStatus {
  employeeId: string;
  employeeName: string;
  badgeNumber: string;
  department: string;
  warningLevel: string;
  infractions30Days: number;
  infractions90Days: number;
  infractionsTotal: number;
  status: string;
  lastInfractionDate: string;
}

// Modal Component
function Modal({ isOpen, onClose, title, children, size = 'md' }: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  if (!isOpen) return null;
  const sizeClass = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' }[size];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className={`relative bg-white rounded-xl shadow-2xl ${sizeClass} w-full`}>
          <div className="border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-6 max-h-[80vh] overflow-y-auto">{children}</div>
        </div>
      </div>
    </div>
  );
}

// Warning Level Badge
function WarningLevelBadge({ level }: { level: string }) {
  const colors: Record<string, string> = {
    none: 'bg-green-100 text-green-800',
    verbal: 'bg-yellow-100 text-yellow-800',
    written: 'bg-orange-100 text-orange-800',
    final: 'bg-red-100 text-red-800',
    suspended: 'bg-red-200 text-red-900',
    // Warning types use a different vocabulary from status levels.
    suspension: 'bg-red-200 text-red-900',
  };
  return (
    <span className={clsx('px-2 py-1 rounded-full text-xs font-medium', colors[level] || colors.none)}>
      {level === 'none' ? 'Clean' : level.charAt(0).toUpperCase() + level.slice(1)}
    </span>
  );
}

// Severity Badge
function SeverityBadge({ severity }: { severity: string }) {
  const colors: Record<string, string> = {
    minor: 'bg-blue-100 text-blue-700',
    moderate: 'bg-yellow-100 text-yellow-700',
    major: 'bg-orange-100 text-orange-700',
    severe: 'bg-red-100 text-red-700',
  };
  return (
    <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', colors[severity] || colors.minor)}>
      {severity}
    </span>
  );
}

// Shown in place of a list that failed to load. "Couldn't load" and
// "nothing here" are different facts; never render a failure as empty.
function LoadError({ what, message, onRetry }: { what: string; message: string; onRetry: () => void }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between gap-4">
      <div>
        <div className="font-medium">Couldn't load {what}</div>
        <div className="text-sm text-red-600">{message}</div>
      </div>
      <button onClick={onRetry} className="px-3 py-1 bg-white border border-red-300 text-red-700 text-sm rounded hover:bg-red-100">
        Retry
      </button>
    </div>
  );
}

// Acknowledgement method + signature date. Shared by Issue Warning and the draft form.
function AckMethodFields({ method, signedAt, onChange, missingDate }: {
  method: string;
  signedAt: string;
  onChange: (v: { method: string; signedAt: string }) => void;
  missingDate: boolean;
}) {
  const hint = ACK_METHODS.find(m => m.value === method)?.hint;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div className="sm:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">Acknowledgement</label>
        <select
          value={method}
          onChange={e => onChange({ method: e.target.value, signedAt })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        >
          {ACK_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
      </div>
      {method === 'signed_paper_on_file' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date signed <span className="text-red-600">*</span></label>
          <input
            type="date"
            value={signedAt}
            max={todayIso()}
            onChange={e => onChange({ method, signedAt: e.target.value })}
            className={clsx('w-full px-3 py-2 border rounded-lg', missingDate ? 'border-red-500 ring-1 ring-red-500 bg-red-50' : 'border-gray-300')}
          />
        </div>
      )}
    </div>
  );
}

// Correction path for an infraction approved in error. Never deletes: the row
// stays in Infraction History marked reversed, with who, when and why, and it
// stops counting toward the employee's totals and escalation.
function ReverseInfractionModal({ infraction, onClose, onDone }: {
  infraction: Infraction;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setTried(true);
    if (!reason.trim()) { setError('A reason is required to reverse an infraction.'); return; }
    setSaving(true);
    setError('');
    try {
      await apiRequest(`/discipline/admin/infractions/${infraction.id}/reverse`, { method: 'POST', body: JSON.stringify({ reason }) });
      onDone();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Reverse infraction" size="md">
      <div className="space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}
        <div className="text-sm text-gray-700">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{infraction.employeeName}</span>
            <span>{infraction.infractionTypeName}</span>
            <SeverityBadge severity={infraction.severity} />
          </div>
          {infraction.description && <div className="text-gray-600 mt-1">{infraction.description}</div>}
        </div>
        <div className="bg-gray-50 border rounded-lg px-3 py-2 text-sm text-gray-700">
          Reversing removes this infraction from {infraction.employeeName}'s 30-day, 90-day and lifetime totals and from escalation. It is not deleted: it stays in Infraction History marked reversed, with your name, the date, and the reason below. Any warning already issued is not changed — rescind that separately if it was based on this.
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason for reversing <span className="text-red-600">*</span></label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Recorded against the wrong employee; the order defect was a supplier issue."
            className={clsx('w-full px-3 py-2 border rounded-lg', tried && !reason.trim() ? 'border-red-500 ring-1 ring-red-500 bg-red-50' : 'border-gray-300')}
          />
        </div>
        <div className="flex justify-end gap-3 pt-2 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50">
            {saving ? 'Reversing...' : 'Reverse infraction'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Correction path for a warning issued in error. Never deletes: the warning
// stays in Warning History labelled rescinded, with who, when and why.
function RescindWarningModal({ warning, onClose, onDone }: {
  warning: Warning;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setTried(true);
    if (!reason.trim()) { setError('A reason is required to rescind a warning.'); return; }
    setSaving(true);
    setError('');
    try {
      await apiRequest(`/discipline/admin/warnings/${warning.id}/rescind`, { method: 'POST', body: JSON.stringify({ reason }) });
      onDone();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Rescind warning" size="md">
      <div className="space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}
        <div className="text-sm text-gray-700">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{warning.employeeName}</span>
            <WarningLevelBadge level={warning.warningType} />
            <span className="text-gray-500">issued {new Date(warning.issuedAt).toLocaleDateString()}</span>
          </div>
          <div className="text-gray-600 mt-1">{warning.reason}</div>
        </div>
        <div className="bg-gray-50 border rounded-lg px-3 py-2 text-sm text-gray-700">
          Rescinding removes this warning from {warning.employeeName}'s standing and from future notices. It is not deleted: it stays in Warning History marked rescinded, with your name, the date, and the reason below.
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason for rescinding <span className="text-red-600">*</span></label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Issued to the wrong employee; incident was later found not to have happened."
            className={clsx('w-full px-3 py-2 border rounded-lg', tried && !reason.trim() ? 'border-red-500 ring-1 ring-red-500 bg-red-50' : 'border-gray-300')}
          />
        </div>
        <div className="flex justify-end gap-3 pt-2 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50">
            {saving ? 'Rescinding...' : 'Rescind warning'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Acknowledgement state of a record, plus the employee's own words and any
// dispute. "Not yet presented" is a valid state, not a problem.
const ACK_LABELS: Record<string, string> = {
  not_yet_presented: 'Not yet presented',
  acknowledged_in_person: 'Acknowledged in person',
  signed_paper_on_file: 'Signed paper on file',
  refused_to_sign: 'Refused to sign',
  acknowledged_in_pwa: 'Acknowledged in workstation app',
};

function AckStatus({ record }: { record: { acknowledgementMethod?: string | null; acknowledgedAt?: string | null; refusedAt?: string | null; employeeComment?: string | null; disputedAt?: string | null; disputeReviewedAt?: string | null; disputeReviewedByName?: string | null; disputeReviewNote?: string | null } }) {
  const method = record.acknowledgementMethod || 'not_yet_presented';
  const when = record.refusedAt || record.acknowledgedAt;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1 flex-wrap">
        <span className={clsx(
          'px-2 py-0.5 rounded text-xs whitespace-nowrap',
          method === 'not_yet_presented' ? 'bg-gray-50 text-gray-500 border border-gray-200' :
          method === 'refused_to_sign' ? 'bg-amber-50 text-amber-800 border border-amber-300' :
          'bg-green-50 text-green-700 border border-green-200'
        )}>
          {ACK_LABELS[method] || method}{when ? ` · ${new Date(when).toLocaleDateString()}` : ''}
        </span>
        {record.disputedAt && (
          <span className={clsx(
            'px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap',
            record.disputeReviewedAt ? 'bg-gray-100 text-gray-700 border border-gray-300' : 'bg-red-600 text-white'
          )}>
            {record.disputeReviewedAt ? 'Dispute reviewed' : 'Disputed'}
          </span>
        )}
      </div>
      {record.employeeComment && (
        <div className="text-xs text-gray-700 bg-gray-50 border rounded px-2 py-1 max-w-xs whitespace-pre-wrap">
          <span className="font-semibold">Employee:</span> {record.employeeComment}
        </div>
      )}
      {record.disputeReviewedAt && record.disputeReviewNote && (
        <div className="text-xs text-gray-600 max-w-xs whitespace-pre-wrap">
          <span className="font-semibold">Reviewed{record.disputeReviewedByName ? ` by ${record.disputeReviewedByName}` : ''}:</span> {record.disputeReviewNote}
        </div>
      )}
    </div>
  );
}

// Acknowledgement recorded after the fact — e.g. the signed copy came back.
// Without it a scanned signature sits next to a record saying "not yet presented".
function RecordAcknowledgementModal({ target, onClose, onDone }: {
  target: { kind: 'infraction' | 'warning'; id: string; title: string; current?: string | null };
  onClose: () => void;
  onDone: () => void;
}) {
  const [method, setMethod] = useState(target.current && target.current !== 'acknowledged_in_pwa' ? target.current : 'signed_paper_on_file');
  const [signedAt, setSignedAt] = useState(todayIso());
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setTried(true);
    if (method === 'signed_paper_on_file' && !signedAt) { setError('Enter the date it was signed.'); return; }
    setSaving(true);
    setError('');
    try {
      await apiRequest(`/discipline/admin/${target.kind === 'infraction' ? 'infractions' : 'warnings'}/${target.id}/acknowledgement`, {
        method: 'POST',
        body: JSON.stringify({ method, ...(method === 'signed_paper_on_file' ? { acknowledgedAt: signedAt } : {}) }),
      });
      onDone();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Record acknowledgment — ${target.title}`} size="md">
      <div className="space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}
        <AckMethodFields
          method={method}
          signedAt={signedAt}
          onChange={v => { setMethod(v.method); setSignedAt(v.signedAt); }}
          missingDate={tried && method === 'signed_paper_on_file' && !signedAt}
        />
        <div className="text-xs text-gray-500">
          If the employee acknowledged in the PWA, that stays as their own record and can't be changed here.
        </div>
        <div className="flex justify-end gap-3 pt-2 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save acknowledgment'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// A disputed record waiting for someone to read it. A dispute doesn't pause
// escalation, so it must not sit unread.
function DisputeCard({ dispute, onDone }: { dispute: OpenDispute; onDone: () => void }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    if (!note.trim()) { setError('Write what was concluded.'); return; }
    setSaving(true);
    setError('');
    try {
      await apiRequest(`/discipline/admin/${dispute.kind === 'infraction' ? 'infractions' : 'warnings'}/${dispute.id}/dispute-review`, {
        method: 'POST', body: JSON.stringify({ note }),
      });
      onDone();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const dateLabel = dispute.date ? new Date(`${dispute.date}T00:00:00`).toLocaleDateString() : '';
  const age = dispute.ageDays ?? 0;
  const isStale = age > STALE_DISPUTE_DAYS;
  return (
    <div className={clsx(
      'border rounded-lg p-4 bg-white border-l-8',
      isStale ? 'border-amber-300 border-l-amber-500' : 'border-red-300 border-l-red-500'
    )}>
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <span className="font-medium text-gray-900">{dispute.employeeName}</span>
        <span className="text-gray-500">{dispute.kind === 'infraction' ? `Incident · ${dispute.title}` : warningTypeLabel(dispute.title)}</span>
        {dateLabel && <span className="text-gray-400">{dateLabel}</span>}
        <span className="ml-auto text-right">
          {isStale ? (
            // The employee said "that's not what happened" and nobody has answered.
            <span className="inline-block px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-300 text-sm font-bold">
              Unanswered — {age} days
            </span>
          ) : (
            <span className="text-xs text-gray-500">{age === 0 ? 'Disputed today' : `Disputed ${age} day${age === 1 ? '' : 's'} ago`}</span>
          )}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-sm">
        <div className="bg-gray-50 border rounded p-2">
          <div className="text-xs font-medium text-gray-500 mb-1">As recorded</div>
          <div className="text-gray-800 whitespace-pre-wrap">{dispute.recordText || <span className="italic text-gray-400">No description was recorded.</span>}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded p-2">
          <div className="text-xs font-medium text-red-700 mb-1">Employee's account</div>
          <div className="text-gray-800 whitespace-pre-wrap">{dispute.employeeComment}</div>
        </div>
      </div>
      <div className="text-xs text-gray-500 mt-2">
        A dispute doesn't reverse or pause anything; the record still counts. Reverse or rescind it separately if the employee is right.
      </div>
      <div className="mt-3 flex flex-col md:flex-row gap-2">
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="What did you conclude?" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        <button onClick={save} disabled={saving} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 text-sm self-start">
          {saving ? 'Saving...' : 'Mark reviewed'}
        </button>
      </div>
      {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
    </div>
  );
}

// A printable document to hand over: the warning notice or the incident record.
// Generated server-side from the record; the API's refusal messages (draft,
// rescinded, termination, suspension / pending, rejected, reversed) are shown
// as-is. docs/WARNING_NOTICE_PDF_SPEC.md, docs/INFRACTION_RECORD_PDF_SPEC.md
interface PrintableDoc {
  kind: 'warning' | 'infraction';
  id: string;
  employeeName: string;
  /** e.g. "Written warning" / "Insubordination" — for follow-on dialogs */
  label: string;
  acknowledgementMethod?: string | null;
}

function PrintNoticeModal({ doc, justIssued, onClose, onAttach, onRecordAck }: {
  doc: PrintableDoc;
  // Opened automatically right after issuing or recording. A document is only
  // useful if it's in someone's hand during the conversation; one you have to
  // go find in the history later is one nobody prints.
  justIssued?: boolean;
  onClose: () => void;
  onAttach: () => void;
  onRecordAck: () => void;
}) {
  const [state, setState] = useState<{ loading: boolean; error?: string; url?: string; filename?: string }>({ loading: true });

  useEffect(() => {
    let url: string | undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('admin_auth_token');
        const path = doc.kind === 'warning' ? `warnings/${doc.id}/notice` : `infractions/${doc.id}/record`;
        const response = await fetch(`${API_BASE}/discipline/admin/${path}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({ message: `Couldn't generate the document (${response.status})` }));
          throw new Error(Array.isArray(err.message) ? err.message.join('; ') : err.message);
        }
        const filename = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') || '')?.[1] || `${doc.kind === 'warning' ? 'warning-notice' : 'incident-record'}.pdf`;
        url = URL.createObjectURL(await response.blob());
        if (!cancelled) setState({ loading: false, url, filename });
      } catch (err: any) {
        if (!cancelled) setState({ loading: false, error: err.message });
      }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [doc.kind, doc.id]);

  const isWarning = doc.kind === 'warning';
  return (
    <Modal isOpen onClose={onClose} title={`${isWarning ? 'Warning notice' : 'Incident record'} — ${doc.employeeName}`} size="md">
      {justIssued && (
        <div className="mb-4 bg-green-50 border border-green-300 text-green-900 px-4 py-3 rounded-lg">
          <div className="font-semibold">
            {isWarning
              ? (state.error ? 'Warning issued.' : 'Warning issued. Print it now and take it with you.')
              : (state.error ? 'Incident recorded.' : 'Incident recorded. Print it now and take it with you.')}
          </div>
          {!isWarning && !state.error && (
            <div className="text-sm mt-1">Not every incident needs a signature. {doc.employeeName} can also acknowledge it themselves in the workstation app.</div>
          )}
        </div>
      )}
      {state.loading ? (
        <div className="py-8 flex flex-col items-center gap-2 text-sm text-gray-500"><LoadingSpinner />Generating…</div>
      ) : state.error ? (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-300 text-amber-900 px-4 py-3 rounded-lg">{state.error}</div>
          <div className="flex justify-end">
            <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Close</button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 text-blue-900 px-4 py-3 rounded-lg font-medium">
            Print, sign, then attach the scanned copy to this record.
          </div>
          <div className="text-sm text-gray-600">{state.filename}</div>
          <div className="flex flex-wrap justify-end gap-3">
            <button onClick={onRecordAck} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
              Record acknowledgment
            </button>
            <button onClick={onAttach} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
              Attach signed scan
            </button>
            <a href={state.url} download={state.filename} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
              Download
            </a>
            <button onClick={() => window.open(state.url, '_blank')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Open to print
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function AttachmentsButton({ count, onClick }: { count?: number; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title={count ? `${count} attachment${count === 1 ? '' : 's'}` : 'Add attachment'}
      className={clsx('px-2 py-0.5 rounded text-xs border', count ? 'border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100' : 'border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50')}
    >
      📎 {count || '+'}
    </button>
  );
}

function AttachmentInput({ file, onChange, label }: { file: File | null; onChange: (f: File | null) => void; label: string }) {
  const problem = attachmentProblem(file);
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label} <span className="text-gray-400 font-normal">(optional — JPG, PNG or PDF, up to 10 MB)</span></label>
      <input
        type="file"
        accept={ATTACHMENT_ACCEPT}
        onChange={e => onChange(e.target.files?.[0] || null)}
        className="block w-full text-sm text-gray-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border file:border-gray-300 file:bg-white file:text-gray-700 hover:file:bg-gray-50"
      />
      {problem && <div className="text-xs text-red-600 mt-1">{problem}</div>}
    </div>
  );
}

// List a record's attachments, open them, and add one later — e.g. the signed
// scan that arrives days after the warning was entered.
function AttachmentsModal({ target, onClose, onChanged }: {
  target: { infractionId?: string; warningId?: string; title: string };
  onClose: () => void;
  onChanged: () => void;
}) {
  const [items, setItems] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const query = target.infractionId ? `infractionId=${target.infractionId}` : `warningId=${target.warningId}`;

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      setItems((await apiRequest(`/discipline/admin/attachments?${query}`)) || []);
    } catch (err: any) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [query]);

  const upload = async () => {
    if (!file || attachmentProblem(file)) return;
    setUploading(true);
    setUploadError('');
    try {
      await uploadAttachment(file, { infractionId: target.infractionId, warningId: target.warningId });
      setFile(null);
      await load();
      onChanged();
    } catch (err: any) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Attachments — ${target.title}`} size="md">
      <div className="space-y-4">
        {loading ? (
          <div className="py-6 flex justify-center"><LoadingSpinner /></div>
        ) : loadError ? (
          <LoadError what="attachments" message={loadError} onRetry={load} />
        ) : items.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-4">No attachments on this record.</div>
        ) : (
          <ul className="divide-y border rounded-lg">
            {items.map(a => (
              <li key={a.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 truncate">{a.originalFilename}</div>
                  <div className="text-xs text-gray-500">
                    {a.mimeType === 'application/pdf' ? 'PDF' : 'Image'} · {Math.max(1, Math.round(a.fileSize / 1024))} KB · {new Date(a.createdAt).toLocaleDateString()}
                    {a.uploadedByName ? ` · ${a.uploadedByName}` : ''}
                  </div>
                </div>
                <button onClick={() => openAttachment(a.id)} className="px-3 py-1 text-blue-600 border border-blue-200 rounded hover:bg-blue-50">Open</button>
              </li>
            ))}
          </ul>
        )}
        <div className="border-t pt-4 space-y-2">
          {uploadError && <div className="bg-red-50 text-red-700 px-3 py-2 rounded-lg text-sm">{uploadError}</div>}
          <AttachmentInput file={file} onChange={setFile} label="Add attachment" />
          <div className="flex justify-end">
            <button
              onClick={upload}
              disabled={!file || !!attachmentProblem(file) || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// Draft warning awaiting review. Three things must be visible without reading
// field values: lifetime-max drafts, stale drafts, and text written for a
// different severity than the draft now carries.
function DraftWarningCard({ draft, onReview }: { draft: Warning; onReview: () => void }) {
  const isLifetimeMax = draft.escalationTrigger === 'lifetime_max';
  const age = draft.ageDays ?? 0;
  const isStale = age > STALE_DRAFT_DAYS;
  const typeChanged = draft.suggestedWarningType && draft.suggestedWarningType !== draft.warningType;
  const hasText = !!(draft.expectationGoingForward?.trim() || draft.consequenceIfNotCorrected?.trim());
  const infractionCount = draft.infractionIds?.length || 0;

  return (
    <div className={clsx(
      'border rounded-lg overflow-hidden',
      isLifetimeMax ? 'border-red-400 border-l-8 border-l-red-600 bg-red-50' :
      isStale ? 'border-amber-300 border-l-8 border-l-amber-500' :
      'border-gray-200 border-l-8 border-l-blue-400'
    )}>
      {isLifetimeMax && (
        <div className="bg-red-600 text-white px-4 py-2 flex items-center gap-2">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="font-bold uppercase tracking-wide text-sm">Lifetime maximum reached</span>
          <span className="text-red-100 text-sm">— not a routine escalation. Nothing has been issued; a person decides what this becomes.</span>
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-medium text-gray-900">{draft.employeeName}</span>
              {draft.badgeNumber && <span className="text-gray-400">({draft.badgeNumber})</span>}
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">DRAFT — not issued</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap text-sm">
              <span className="text-gray-500">Draft set to</span>
              <WarningLevelBadge level={draft.warningType} />
              {typeChanged && (
                <span className="text-gray-500">(escalation suggested <span className="font-medium text-gray-700">{warningTypeLabel(draft.suggestedWarningType)}</span>)</span>
              )}
            </div>
          </div>

          <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
            {isStale ? (
              <span className="inline-block px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-300 text-sm font-bold">
                Stale — {age} days old
              </span>
            ) : (
              <span className="text-sm text-gray-500">{age === 0 ? 'Raised today' : `Raised ${age} day${age === 1 ? '' : 's'} ago`}</span>
            )}
            <div className="text-xs text-gray-400">{new Date(draft.createdAt).toLocaleDateString()}</div>
            <button
              onClick={onReview}
              className={clsx(
                'mt-1 px-3 py-1.5 text-white text-sm rounded-lg',
                isLifetimeMax ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
              )}
            >
              Review &amp; issue
            </button>
          </div>
        </div>

        {draft.textLevelMismatch && (
          <div className="mt-3 bg-amber-50 border border-amber-300 text-amber-900 px-3 py-2 rounded-lg text-sm flex gap-2">
            <svg className="w-5 h-5 flex-shrink-0 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <span className="font-bold">Re-read before issuing.</span> This text was written for a{' '}
              <span className="font-semibold">{warningTypeLabel(draft.textWrittenForType)}</span>; severity has since changed to{' '}
              <span className="font-semibold">{warningTypeLabel(draft.warningType)}</span>.
            </div>
          </div>
        )}

        {draft.escalationNote && (
          <div className="mt-3 text-xs text-gray-600 bg-gray-50 border rounded px-2 py-1">
            <span className="font-semibold">Why this draft exists:</span> {draft.escalationNote}
            <span className="block text-gray-400 mt-0.5">Internal note — never printed. Write the reason when you issue.</span>
          </div>
        )}
        {infractionCount > 0 && (
          <div className="text-xs text-gray-500 mt-1">
            {infractionCount} linked infraction{infractionCount === 1 ? '' : 's'}
            {(draft.linkedDisputedCount ?? 0) > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded bg-red-600 text-white font-semibold">{draft.linkedDisputedCount} disputed</span>
            )}
          </div>
        )}

        {hasText ? (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="bg-white border rounded p-2">
              <div className="text-xs font-medium text-gray-500 mb-1">Expectation going forward</div>
              <div className="text-gray-800 whitespace-pre-wrap">{draft.expectationGoingForward || <span className="text-gray-400 italic">Not written</span>}</div>
            </div>
            <div className="bg-white border rounded p-2">
              <div className="text-xs font-medium text-gray-500 mb-1">Consequence if not corrected</div>
              <div className="text-gray-800 whitespace-pre-wrap">{draft.consequenceIfNotCorrected || <span className="text-gray-400 italic">Not written</span>}</div>
            </div>
          </div>
        ) : (
          <div className="mt-3 text-xs text-gray-400 italic">No expectation or consequence written yet.</div>
        )}
      </div>
    </div>
  );
}

// Review a draft warning, then save it or issue it.
//
// warning_type is deliberately editable in both directions: escalation only
// suggests. The linked infractions sit beside the text fields so the person
// issuing reads what the warning is based on, not the one-line summary.
function IssueDraftModal({
  draft, employees, settings, onClose, onDone,
}: {
  draft: Warning;
  employees: any[];
  settings: any;
  onClose: () => void;
  // `issued` is set when the draft was issued (not just saved), so the page can
  // open the printable notice immediately.
  onDone: (notice?: string, issued?: Warning) => void;
}) {
  const [form, setForm] = useState({
    warningType: draft.warningType,
    reason: '',  // escalation drafts carry no printable reason; the person writes it
    expectation: draft.expectationGoingForward || '',
    consequence: draft.consequenceIfNotCorrected || '',
    incidentDate: draft.incidentDate ? String(draft.incidentDate).slice(0, 10) : '',
    witnessEmployeeId: draft.witnessEmployeeId || '',
    issuedBy: draft.issuedBy || '',
    suspensionDays: settings.suspension_defaults?.first_suspension_days || 3,
    ackMethod: 'not_yet_presented',
    ackSignedAt: todayIso(),
  });
  const [file, setFile] = useState<File | null>(null);
  const [textTouched, setTextTouched] = useState(false);
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [saving, setSaving] = useState<'draft' | 'issue' | null>(null);
  const [error, setError] = useState('');

  const [infractions, setInfractions] = useState<Infraction[]>([]);
  const [infLoading, setInfLoading] = useState(true);
  const [infError, setInfError] = useState('');

  const linkedIds = draft.infractionIds || [];
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows: Infraction[] = await apiRequest(`/discipline/admin/infractions?employeeId=${draft.employeeId}&limit=1000`);
        if (!cancelled) setInfractions(rows || []);
      } catch (err: any) {
        if (!cancelled) setInfError(err?.message || 'Request failed');
      } finally {
        if (!cancelled) setInfLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [draft.id]);

  // With linked ids, show exactly those. Without (lifetime-max drafts carry
  // none), show the employee's whole approved record, and say so.
  const shownInfractions = (linkedIds.length > 0
    ? infractions.filter(i => linkedIds.includes(i.id))
    : infractions.filter(i => i.status === 'approved')
  ).sort((a, b) => String(b.incidentDate).localeCompare(String(a.incidentDate)));
  const missingLinked = linkedIds.length > 0 ? linkedIds.length - shownInfractions.length : 0;

  const isLifetimeMax = draft.escalationTrigger === 'lifetime_max';
  const suggested = draft.suggestedWarningType || draft.warningType;
  const direction = WARNING_TYPE_ORDER.indexOf(form.warningType) - WARNING_TYPE_ORDER.indexOf(suggested);
  const textRequired = TYPES_REQUIRING_TEXT.includes(form.warningType);
  const missing = [
    ...missingRequiredFields(form),
    ...(form.ackMethod === 'signed_paper_on_file' && !form.ackSignedAt ? ['Date signed'] : []),
  ];
  const hasText = !!(form.expectation.trim() || form.consequence.trim());
  const textBasis = draft.textWrittenForType;
  const showMismatch = hasText && !textTouched && !!textBasis && textBasis !== form.warningType;
  const expiryDays = settings.warning_expiration?.[`${form.warningType}_days`];
  const fieldError = (name: string) => triedSubmit && missing.includes(name);
  const inputClass = (name: string) => clsx(
    'w-full px-3 py-2 border rounded-lg',
    fieldError(name) ? 'border-red-500 ring-1 ring-red-500 bg-red-50' : 'border-gray-300'
  );

  const payload = () => ({
    warningType: form.warningType,
    reason: form.reason,
    expectationGoingForward: form.expectation,
    consequenceIfNotCorrected: form.consequence,
    incidentDate: form.incidentDate || undefined,
    witnessEmployeeId: form.witnessEmployeeId || undefined,
    issuedBy: form.issuedBy || undefined,
    ...(form.warningType === 'suspension' ? { suspensionDays: form.suspensionDays } : {}),
  });

  // The draft already has an id, so an attachment can go on at save or issue.
  // If the record saved but the file didn't, say so on the page rather than
  // keeping the form open (a retry would re-submit the record).
  const attachIfAny = async (): Promise<string | undefined> => {
    if (!file) return undefined;
    try {
      await uploadAttachment(file, { warningId: draft.id });
      return undefined;
    } catch (err: any) {
      return `${draft.employeeName}'s warning was saved, but the attachment failed to upload: ${err.message}. Add it from Warning History (📎).`;
    }
  };

  const saveDraft = async () => {
    if (attachmentProblem(file)) { setError(attachmentProblem(file)!); return; }
    setSaving('draft');
    setError('');
    try {
      await apiRequest(`/discipline/admin/warnings/${draft.id}/draft`, { method: 'PATCH', body: JSON.stringify(payload()) });
      onDone(await attachIfAny());
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const issue = async () => {
    setTriedSubmit(true);
    if (missing.length > 0) {
      setError(`To issue this ${warningTypeLabel(form.warningType)}, fill in: ${missing.join(', ')}.`);
      return;
    }
    if (attachmentProblem(file)) { setError(attachmentProblem(file)!); return; }
    // Speed bump, not a block: written/final/suspension are meant to be handed over
    // in person. If they'd go out unpresented, confirm once — but let them continue,
    // because a hard block just gets worked around by falsely marking "in person".
    if (TYPES_REQUIRING_TEXT.includes(form.warningType) && form.ackMethod === 'not_yet_presented' &&
        !window.confirm(`A ${warningTypeLabel(form.warningType)} is meant to be delivered and signed in person. This one is set to "Not yet presented." Issue it anyway?`)) {
      return;
    }
    setSaving('issue');
    setError('');
    try {
      const issued = await apiRequest(`/discipline/admin/warnings/${draft.id}/issue`, {
        method: 'POST',
        body: JSON.stringify({
          ...payload(),
          acknowledgementMethod: form.ackMethod,
          ...(form.ackMethod === 'signed_paper_on_file' ? { acknowledgedAt: form.ackSignedAt } : {}),
        }),
      });
      onDone(await attachIfAny(), { ...issued, employeeName: draft.employeeName });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Review draft — ${draft.employeeName}`} size="xl">
      <div className="space-y-4">
        {isLifetimeMax && (
          <div className="bg-red-600 text-white px-4 py-3 rounded-lg">
            <div className="font-bold uppercase tracking-wide text-sm">Lifetime maximum reached</div>
            <div className="text-sm text-red-100 mt-1">
              Escalation suggested the highest warning. You decide what this becomes — a warning here, or a separation (opened separately in the Separation module). Nothing is created automatically.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Linked infractions */}
          <div className="lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              {linkedIds.length > 0 ? `Based on ${linkedIds.length} infraction${linkedIds.length === 1 ? '' : 's'}` : 'Infraction record'}
              {(draft.linkedDisputedCount ?? 0) > 0 && (
                <span className="ml-2 normal-case px-1.5 py-0.5 rounded bg-red-600 text-white text-xs">{draft.linkedDisputedCount} disputed</span>
              )}
            </h3>
            {draft.escalationNote && (
              <div className="text-xs text-gray-500 mb-2"><span className="font-semibold">Why:</span> {draft.escalationNote} <span className="italic">(internal — not printed)</span></div>
            )}
            {infLoading ? (
              <div className="py-6 flex justify-center"><LoadingSpinner /></div>
            ) : infError ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                <div className="font-medium">Couldn't load the infractions this draft is based on</div>
                <div>{infError}</div>
                <div className="mt-1 font-medium">Don't issue until you can see them.</div>
              </div>
            ) : (
              <div className="space-y-2 lg:max-h-[60vh] lg:overflow-y-auto pr-1">
                {linkedIds.length === 0 && (
                  <div className="text-xs text-gray-500 bg-gray-50 border rounded px-2 py-1">
                    No infractions are linked to this draft. Showing all {shownInfractions.length} approved infraction{shownInfractions.length === 1 ? '' : 's'} on record.
                  </div>
                )}
                {missingLinked > 0 && (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1">
                    {missingLinked} linked infraction{missingLinked === 1 ? '' : 's'} could not be found (deleted, or not on this employee's record).
                  </div>
                )}
                {shownInfractions.length === 0 && (
                  <div className="text-sm text-gray-500 py-4 text-center">No infractions to show.</div>
                )}
                {shownInfractions.map(inf => (
                  <div key={inf.id} className="border rounded-lg p-2 text-sm bg-white">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-900">{inf.infractionTypeName}</span>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {inf.incidentDate ? new Date(String(inf.incidentDate).slice(0, 10) + 'T00:00:00').toLocaleDateString() : '—'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <SeverityBadge severity={inf.severity} />
                      {inf.status !== 'approved' && <span className="text-xs text-gray-500">{inf.status}</span>}
                      {inf.orderNumber && <span className="text-xs text-blue-600">Order {inf.orderNumber}</span>}
                      {inf.disputedAt && <span className="text-xs px-1.5 py-0.5 rounded bg-red-600 text-white font-semibold">Disputed</span>}
                    </div>
                    {inf.description && <div className="text-gray-600 mt-1 whitespace-pre-wrap">{inf.description}</div>}
                    {inf.disputedAt && inf.employeeComment && (
                      <div className="mt-1 text-xs bg-red-50 border border-red-200 rounded px-2 py-1 text-gray-800 whitespace-pre-wrap">
                        <span className="font-semibold text-red-700">Employee disputes:</span> {inf.employeeComment}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form */}
          <div className="lg:col-span-3 space-y-4">
            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Warning type — your decision</label>
              <div className="flex flex-wrap gap-2">
                {WARNING_TYPE_ORDER.map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm({ ...form, warningType: t })}
                    className={clsx(
                      'px-3 py-1.5 rounded-lg border text-sm',
                      form.warningType === t ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    )}
                  >
                    {warningTypeLabel(t).replace(/^./, c => c.toUpperCase())}
                    {t === suggested && <span className={clsx('ml-1 text-xs', form.warningType === t ? 'text-blue-100' : 'text-gray-400')}>(suggested)</span>}
                  </button>
                ))}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {direction === 0
                  ? `Escalation suggested ${warningTypeLabel(suggested)}. You can issue higher or lower; both are kept on the record.`
                  : `Issuing ${direction < 0 ? 'below' : 'above'} the suggested ${warningTypeLabel(suggested)}. Both values are kept on the record.`}
              </div>
            </div>

            {form.warningType === 'suspension' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Suspension days</label>
                <input
                  type="number" min={1} max={30}
                  value={form.suspensionDays}
                  onChange={e => setForm({ ...form, suspensionDays: parseInt(e.target.value) || 1 })}
                  onWheel={e => e.currentTarget.blur()}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason <span className="text-red-600">*</span> <span className="text-gray-400 font-normal">— write it; escalation notes are internal and never print</span>
              </label>
              <textarea
                value={form.reason}
                onChange={e => setForm({ ...form, reason: e.target.value })}
                rows={2}
                className={inputClass('Reason')}
              />
            </div>

            {showMismatch && (
              <div className="bg-amber-50 border border-amber-300 text-amber-900 px-3 py-2 rounded-lg text-sm">
                <span className="font-bold">Re-read before issuing.</span> This text was written for a{' '}
                <span className="font-semibold">{warningTypeLabel(textBasis)}</span>; you're issuing a{' '}
                <span className="font-semibold">{warningTypeLabel(form.warningType)}</span>.
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expectation going forward{' '}
                {textRequired ? <span className="text-red-600">*</span>
                  : <span className="text-gray-400 font-normal">(optional for a {warningTypeLabel(form.warningType)})</span>}
              </label>
              <textarea
                value={form.expectation}
                onChange={e => { setTextTouched(true); setForm({ ...form, expectation: e.target.value }); }}
                rows={3}
                placeholder="What specifically needs to change?"
                className={inputClass('Expectation going forward')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Consequence if not corrected{' '}
                {textRequired ? <span className="text-red-600">*</span>
                  : <span className="text-gray-400 font-normal">(optional for a {warningTypeLabel(form.warningType)})</span>}
              </label>
              <textarea
                value={form.consequence}
                onChange={e => { setTextTouched(true); setForm({ ...form, consequence: e.target.value }); }}
                rows={3}
                placeholder="What happens if it doesn't?"
                className={inputClass('Consequence if not corrected')}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Incident date</label>
                <input
                  type="date"
                  value={form.incidentDate}
                  onChange={e => setForm({ ...form, incidentDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delivered by</label>
                <select
                  value={form.issuedBy}
                  onChange={e => setForm({ ...form, issuedBy: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Me (signed-in user)</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Witness</label>
                <select
                  value={form.witnessEmployeeId}
                  onChange={e => setForm({ ...form, witnessEmployeeId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">None</option>
                  {employees.filter(e => e.id !== draft.employeeId).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
            </div>

            <AckMethodFields
              method={form.ackMethod}
              signedAt={form.ackSignedAt}
              onChange={v => setForm({ ...form, ackMethod: v.method, ackSignedAt: v.signedAt })}
              missingDate={fieldError('Date signed')}
            />
            <div className="text-xs text-gray-500 -mt-2">Acknowledgement is recorded when you issue, not when you save the draft.</div>

            <AttachmentInput file={file} onChange={setFile} label="Attachment" />
            {(draft.attachmentCount ?? 0) > 0 && (
              <div className="text-xs text-gray-500 -mt-2">This draft already has {draft.attachmentCount} attachment{draft.attachmentCount === 1 ? '' : 's'}.</div>
            )}

            <div className="text-sm text-gray-600 bg-gray-50 border rounded-lg px-3 py-2">
              Issuing records a <span className="font-semibold">{warningTypeLabel(form.warningType)}</span> against {draft.employeeName} now
              {expiryDays ? `, expiring ${expiryDays} days from today` : ''}, and sends it for acknowledgement.
              Saving the draft changes nothing on their record.
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t">
              <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                type="button" onClick={saveDraft} disabled={!!saving}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {saving === 'draft' ? 'Saving...' : 'Save draft'}
              </button>
              <button
                type="button" onClick={issue} disabled={!!saving || infLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {saving === 'issue' ? 'Issuing...' : `Issue ${warningTypeLabel(form.warningType)}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// Issue Infraction Modal
function IssueInfractionModal({
  isOpen, onClose, subjects, employees, infractionTypes, pointsEnabled, onSubmit
}: {
  isOpen: boolean;
  onClose: () => void;
  subjects: any[];
  employees: any[];
  infractionTypes: Record<string, InfractionType[]>;
  // Discipline no longer deducts reward points (setting off); don't show a
  // deduction that won't happen.
  pointsEnabled: boolean;
  onSubmit: (data: any, file: File | null) => Promise<void>;
}) {
  const blank = () => ({
    employeeId: '',
    infractionTypeId: '',
    orderNumber: '',
    description: '',
    managerNotes: '',
    incidentDate: todayIso(),
    witnessEmployeeId: '',
  });
  const [form, setForm] = useState(blank);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (isOpen) { setForm(blank()); setFile(null); setError(''); } }, [isOpen]);

  const selectedType = Object.values(infractionTypes).flat().find(t => t.id === form.infractionTypeId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.employeeId || !form.infractionTypeId) {
      setError('Please select an employee and incident type');
      return;
    }
    if (attachmentProblem(file)) { setError(attachmentProblem(file)!); return; }
    setSaving(true);
    setError('');
    try {
      const { witnessEmployeeId, ...rest } = form;
      await onSubmit({ ...rest, ...(witnessEmployeeId ? { witnessEmployeeId } : {}) }, file);
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Issue Infraction" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
          <select
            value={form.employeeId}
            onChange={e => setForm({ ...form, employeeId: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            required
          >
            <option value="">Select employee...</option>
            {subjects.map(e => (
              <option key={e.id} value={e.id}>{e.name}{(e.badgeNumber || e.badge_number) ? ` (${e.badgeNumber || e.badge_number})` : ''}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Incident type *</label>
          <select
            value={form.infractionTypeId}
            onChange={e => setForm({ ...form, infractionTypeId: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            required
          >
            <option value="">Select incident type...</option>
            {Object.entries(infractionTypes).map(([category, types]) => (
              <optgroup key={category} label={category.replace(/_/g, ' ').toUpperCase()}>
                {types.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name}{pointsEnabled ? ` (-${t.pointsDeducted} pts)` : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {selectedType && (
            <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm">
              <div className="flex items-center gap-2 mb-1">
                <SeverityBadge severity={selectedType.severity} />
                {pointsEnabled && <span className="font-medium text-red-600">-{selectedType.pointsDeducted} points</span>}
              </div>
              <p className="text-gray-600">{selectedType.description}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Incident Date</label>
            <input
              type="date"
              value={form.incidentDate}
              max={todayIso()}
              onChange={e => setForm({ ...form, incidentDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Witness</label>
            <select
              value={form.witnessEmployeeId}
              onChange={e => setForm({ ...form, witnessEmployeeId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">None</option>
              {employees.filter(e => e.id !== form.employeeId).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order # (if QC related)</label>
            <input
              type="text"
              value={form.orderNumber}
              onChange={e => setForm({ ...form, orderNumber: e.target.value })}
              placeholder="e.g., KS-12345"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description / Details</label>
          <textarea
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            rows={3}
            placeholder="What happened? Include specific details..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        <AttachmentInput file={file} onChange={setFile} label="Attachment" />

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
            {saving ? 'Issuing...' : 'Issue Infraction'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Issue Warning Modal (manual — not from escalation)
function IssueWarningModal({
  isOpen, onClose, subjects, employees, settings, onSubmit, prefillEmployeeId
}: {
  isOpen: boolean;
  onClose: () => void;
  subjects: any[];
  employees: any[];
  settings: any;
  onSubmit: (data: any, file: File | null) => Promise<void>;
  prefillEmployeeId?: string;
}) {
  const blank = () => ({
    employeeId: '',
    warningType: 'verbal',
    reason: '',
    expectation: '',
    consequence: '',
    incidentDate: todayIso(),
    witnessEmployeeId: '',
    issuedBy: '',
    suspensionDays: settings.suspension_defaults?.first_suspension_days || 3,
    ackMethod: 'not_yet_presented',
    ackSignedAt: todayIso(),
  });
  const [form, setForm] = useState(blank);
  const [file, setFile] = useState<File | null>(null);
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Pre-select the employee when opened from a review's "corrective action" link.
  useEffect(() => { if (isOpen) { setForm({ ...blank(), employeeId: prefillEmployeeId || '' }); setFile(null); setError(''); setTriedSubmit(false); } }, [isOpen, prefillEmployeeId]);

  const textRequired = TYPES_REQUIRING_TEXT.includes(form.warningType);
  const missing = [
    ...(!form.employeeId ? ['Employee'] : []),
    // Reason is required on every manual warning (the column is NOT NULL);
    // termination additionally can't be whitespace.
    ...(!form.reason.trim() ? ['Reason'] : []),
    ...missingRequiredFields(form).filter(f => f !== 'Reason'),
    ...(form.ackMethod === 'signed_paper_on_file' && !form.ackSignedAt ? ['Date signed'] : []),
  ];
  const fieldError = (name: string) => triedSubmit && missing.includes(name);
  const inputClass = (name: string) => clsx(
    'w-full px-3 py-2 border rounded-lg',
    fieldError(name) ? 'border-red-500 ring-1 ring-red-500 bg-red-50' : 'border-gray-300'
  );
  const expiryDays = settings.warning_expiration?.[`${form.warningType}_days`];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTriedSubmit(true);
    if (missing.length > 0) {
      setError(`To issue this ${warningTypeLabel(form.warningType)}, fill in: ${missing.join(', ')}.`);
      return;
    }
    if (attachmentProblem(file)) { setError(attachmentProblem(file)!); return; }
    // Speed bump, not a block (same as the draft path): confirm before a
    // written/final/suspension goes out marked "Not yet presented".
    if (TYPES_REQUIRING_TEXT.includes(form.warningType) && form.ackMethod === 'not_yet_presented' &&
        !window.confirm(`A ${warningTypeLabel(form.warningType)} is meant to be delivered and signed in person. This one is set to "Not yet presented." Issue it anyway?`)) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSubmit({
        employeeId: form.employeeId,
        warningType: form.warningType,
        reason: form.reason,
        ...(form.expectation.trim() ? { expectationGoingForward: form.expectation } : {}),
        ...(form.consequence.trim() ? { consequenceIfNotCorrected: form.consequence } : {}),
        ...(form.incidentDate ? { incidentDate: form.incidentDate } : {}),
        ...(form.witnessEmployeeId ? { witnessEmployeeId: form.witnessEmployeeId } : {}),
        ...(form.issuedBy ? { issuedBy: form.issuedBy } : {}),
        ...(form.warningType === 'suspension' ? { suspensionDays: form.suspensionDays } : {}),
        acknowledgementMethod: form.ackMethod,
        ...(form.ackMethod === 'signed_paper_on_file' ? { acknowledgedAt: form.ackSignedAt } : {}),
      }, file);
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Issue Warning" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
          <select
            value={form.employeeId}
            onChange={e => setForm({ ...form, employeeId: e.target.value })}
            className={inputClass('Employee')}
          >
            <option value="">Select employee...</option>
            {subjects.map(e => (
              <option key={e.id} value={e.id}>{e.name}{(e.badgeNumber || e.badge_number) ? ` (${e.badgeNumber || e.badge_number})` : ''}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Warning Type *</label>
            <select
              value={form.warningType}
              onChange={e => setForm({ ...form, warningType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="verbal">Verbal Warning</option>
              <option value="written">Written Warning</option>
              <option value="final">Final Warning</option>
              <option value="suspension">Suspension</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Incident Date</label>
            <input
              type="date"
              value={form.incidentDate}
              max={todayIso()}
              onChange={e => setForm({ ...form, incidentDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
        {form.warningType === 'suspension' && (
          <div className="sm:w-1/2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Suspension Days</label>
            <input
              type="number" min={1} max={30}
              value={form.suspensionDays}
              onChange={e => setForm({ ...form, suspensionDays: parseInt(e.target.value) || 1 })}
              onWheel={(e) => e.currentTarget.blur()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
          <textarea
            value={form.reason}
            onChange={e => setForm({ ...form, reason: e.target.value })}
            rows={3}
            placeholder="What happened, factually?"
            className={inputClass('Reason')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Expectation going forward{' '}
            {textRequired ? <span className="text-red-600">*</span>
              : <span className="text-gray-400 font-normal">(optional for a {warningTypeLabel(form.warningType)})</span>}
          </label>
          <textarea
            value={form.expectation}
            onChange={e => setForm({ ...form, expectation: e.target.value })}
            rows={2}
            placeholder="What specifically needs to change?"
            className={inputClass('Expectation going forward')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Consequence if not corrected{' '}
            {textRequired ? <span className="text-red-600">*</span>
              : <span className="text-gray-400 font-normal">(optional for a {warningTypeLabel(form.warningType)})</span>}
          </label>
          <textarea
            value={form.consequence}
            onChange={e => setForm({ ...form, consequence: e.target.value })}
            rows={2}
            placeholder="What happens if it doesn't?"
            className={inputClass('Consequence if not corrected')}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Delivered by</label>
            <select
              value={form.issuedBy}
              onChange={e => setForm({ ...form, issuedBy: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Me (signed-in user)</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Witness</label>
            <select
              value={form.witnessEmployeeId}
              onChange={e => setForm({ ...form, witnessEmployeeId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">None</option>
              {employees.filter(e => e.id !== form.employeeId).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
        </div>

        <AckMethodFields
          method={form.ackMethod}
          signedAt={form.ackSignedAt}
          onChange={v => setForm({ ...form, ackMethod: v.method, ackSignedAt: v.signedAt })}
          missingDate={fieldError('Date signed')}
        />

        <AttachmentInput file={file} onChange={setFile} label="Attachment" />

        <div className="text-sm text-gray-600 bg-gray-50 border rounded-lg px-3 py-2">
          Issuing records a <span className="font-semibold">{warningTypeLabel(form.warningType)}</span>
          {expiryDays ? `, expiring ${expiryDays} days from today` : ''}, and moves the employee's standing immediately.
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
            {saving ? 'Issuing...' : `Issue ${warningTypeLabel(form.warningType)}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Main Page
export default function DisciplinePage() {
  const [tab, setTab] = useState<'employees' | 'infractions' | 'warnings' | 'pending'>('employees');
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<any[]>([]);
  const [employeeStatuses, setEmployeeStatuses] = useState<EmployeeStatus[]>([]);
  const [infractions, setInfractions] = useState<Infraction[]>([]);
  const [pendingInfractions, setPendingInfractions] = useState<Infraction[]>([]);
  const [draftWarnings, setDraftWarnings] = useState<Warning[]>([]);
  const [reviewCount, setReviewCount] = useState<PendingReviewCount | null>(null);
  const [warnings, setWarnings] = useState<Warning[]>([]);
  const [infractionTypes, setInfractionTypes] = useState<Record<string, InfractionType[]>>({});
  const [showInfractionModal, setShowInfractionModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningPrefillEmployeeId, setWarningPrefillEmployeeId] = useState('');
  // Deep-link from a review's "corrective action warranted": open Issue Warning
  // with that employee pre-selected. e.g. /hr/discipline?issueWarning=<employeeId>
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('issueWarning');
    if (p) { setWarningPrefillEmployeeId(p); setShowWarningModal(true); }
  }, []);
  const [reviewDraft, setReviewDraft] = useState<Warning | null>(null);
  const [settings, setSettings] = useState<any>({});
  // Per-list load errors, keyed by list. Each list loads independently so one
  // failing endpoint can't blank the whole page.
  const [loadErrors, setLoadErrors] = useState<Record<string, string>>({});
  // 403 from the Discipline endpoints: the signed-in employee lacks the grant.
  // Shown as its own state, not as eight "couldn't load" boxes.
  const [forbidden, setForbidden] = useState<string | null>(null);
  // Outcome that needs to outlive a closed modal (e.g. record saved, file didn't).
  const [notice, setNotice] = useState<string | null>(null);
  const [attachmentsFor, setAttachmentsFor] = useState<{ infractionId?: string; warningId?: string; title: string } | null>(null);
  const [noticeFor, setNoticeFor] = useState<{ doc: PrintableDoc; justIssued: boolean } | null>(null);
  const warningDoc = (w: Warning): PrintableDoc => ({ kind: 'warning', id: w.id, employeeName: w.employeeName, label: warningTypeLabel(w.warningType), acknowledgementMethod: w.acknowledgementMethod });
  const infractionDoc = (i: Infraction): PrintableDoc => ({ kind: 'infraction', id: i.id, employeeName: i.employeeName, label: i.infractionTypeName, acknowledgementMethod: i.acknowledgementMethod });
  const [ackFor, setAckFor] = useState<{ kind: 'infraction' | 'warning'; id: string; title: string; current?: string | null } | null>(null);
  const [disputes, setDisputes] = useState<OpenDispute[]>([]);
  const [rescindFor, setRescindFor] = useState<Warning | null>(null);
  const [reverseFor, setReverseFor] = useState<Infraction | null>(null);

  const loadData = async () => {
    setLoading(true);
    const errors: Record<string, string> = {};
    let forbiddenMessage: string | null = null;
    const load = async <T,>(key: string, endpoint: string, set: (v: T) => void, empty: T) => {
      try {
        set((await apiRequest(endpoint)) || empty);
      } catch (err: any) {
        console.error(`Failed to load ${key}:`, err);
        if (err?.status === 403 && endpoint.startsWith('/discipline/admin/')) forbiddenMessage = err.message;
        errors[key] = err?.message || 'Request failed';
        set(empty);
      }
    };
    await Promise.all([
      load('employees', '/employees?status=active', setEmployees, []),
      load('statuses', '/discipline/admin/employee-statuses', setEmployeeStatuses, []),
      load('infractions', '/discipline/admin/infractions?limit=100', setInfractions, []),
      load('pending', '/discipline/admin/infractions/pending', setPendingInfractions, []),
      load('drafts', '/discipline/admin/warnings/drafts', setDraftWarnings, []),
      load('disputes', '/discipline/admin/disputes', setDisputes, []),
      load<PendingReviewCount | null>('reviewCount', '/discipline/admin/pending-review-count', setReviewCount, null),
      load('warnings', '/discipline/admin/warnings?limit=100', setWarnings, []),
      load('types', '/discipline/admin/infraction-types/by-category', setInfractionTypes, {}),
      load('settings', '/discipline/admin/settings', setSettings, {}),
    ]);
    setLoadErrors(errors);
    setForbidden(forbiddenMessage);
    setLoading(false);
  };

  // Only employees switched on for "Subject to Discipline" can be the subject
  // of a record. Witness / delivered-by pickers still list everyone.
  const subjects = employees.filter(e => e.discipline_subject !== false);
  const pointsEnabled = settings.points_deduction_enabled === true;

  // Pending drafts per employee. Drafts deliberately don't move warning_level,
  // so an employee can read "Clean" with a lifetime-max draft waiting; the
  // Employee Status row shows this beside the level rather than changing it.
  const draftsByEmployee = draftWarnings.reduce<Record<string, { count: number; lifetimeMax: number }>>((acc, d) => {
    const entry = acc[d.employeeId] || (acc[d.employeeId] = { count: 0, lifetimeMax: 0 });
    entry.count++;
    if (d.escalationTrigger === 'lifetime_max') entry.lifetimeMax++;
    return acc;
  }, {});

  // Lists with no tab of their own still surface failures, in a page banner.
  const bannerErrors = [
    loadErrors.employees && { what: 'the employee list (Issue Infraction / Issue Warning dropdowns will be empty)', message: loadErrors.employees },
    loadErrors.types && { what: 'infraction types (Issue Infraction will have no types)', message: loadErrors.types },
    loadErrors.settings && { what: 'discipline settings (lifetime max shown is a default, not your setting)', message: loadErrors.settings },
  ].filter(Boolean) as { what: string; message: string }[];

  useEffect(() => { loadData(); }, []);

  // The record is created first, then the file attached to its id. If the file
  // fails, the record still exists: report it on the page (the modal closes, so
  // nobody re-submits and creates a duplicate).
  const attachAfterCreate = async (file: File | null, parent: { infractionId?: string; warningId?: string }, what: string) => {
    if (!file) return;
    try {
      await uploadAttachment(file, parent);
    } catch (err: any) {
      setNotice(`The ${what} was recorded, but the attachment failed to upload: ${err.message}. Add it from ${parent.infractionId ? 'Infraction' : 'Warning'} History (📎).`);
    }
  };

  // Recorded by someone with Discipline Access: approved at that moment, with
  // them as approver. (Floor submissions stay pending for review.)
  const handleIssueInfraction = async (data: any, file: File | null) => {
    const created = await apiRequest('/discipline/admin/infractions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    await attachAfterCreate(file, { infractionId: created?.id }, 'infraction');
    if (created?.pointsDeduction?.status === 'failed') {
      setNotice(`The infraction was recorded, but reward points were NOT deducted: ${created.pointsDeduction.error || 'unknown error'}.`);
    }
    loadData();
    // Record, then offer the printable straight away.
    if (created?.id) setNoticeFor({ doc: infractionDoc(created), justIssued: true });
  };

  const handleApproveInfraction = async (id: string) => {
    try {
      const result = await apiRequest(`/discipline/admin/infractions/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      // If points were meant to be taken and weren't, the approver has to know.
      if (result?.pointsDeduction?.status === 'failed') {
        setNotice(`The infraction was approved, but reward points were NOT deducted: ${result.pointsDeduction.error || 'unknown error'}.`);
      }
      // Approved: the record is now printable, so offer it immediately.
      if (result?.id) setNoticeFor({ doc: infractionDoc(result), justIssued: true });
    } catch (err: any) {
      setNotice(`Couldn't approve the infraction: ${err.message}`);
    }
    loadData();
  };

  const handleRejectInfraction = async (id: string) => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason) return;
    await apiRequest(`/discipline/admin/infractions/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ adminNotes: reason }),
    });
    loadData();
  };

  const handleIssueWarning = async (data: any, file: File | null) => {
    const created = await apiRequest('/discipline/admin/warnings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    await attachAfterCreate(file, { warningId: created?.id }, 'warning');
    loadData();
    // Straight to the printable notice. The API response has no employee name.
    if (created?.id) {
      const name = employees.find(e => e.id === created.employeeId)?.name || 'employee';
      setNoticeFor({ doc: warningDoc({ ...created, employeeName: name }), justIssued: true });
    }
  };

  if (!loading && forbidden) {
    return (
      <>
        <PageHeader title="Employee Discipline" subtitle="Manage infractions, warnings, and employee standing" />
        <Card>
          <div className="text-center py-10">
            <div className="text-lg font-semibold text-gray-900">You don't have Discipline access</div>
            <div className="text-sm text-gray-600 mt-2 max-w-md mx-auto">
              Discipline records are limited to employees granted access in User Management (Discipline Access toggle).
            </div>
          </div>
        </Card>
      </>
    );
  }

  const lifetimeMax = settings.lifetime_max_infractions?.max || 100;

  return (
    <>
      <PageHeader
        title="Employee Discipline"
        subtitle="Manage infractions, warnings, and employee standing"
        actions={
          <div className="flex gap-2">
            <button
              onClick={() => setShowInfractionModal(true)}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Issue Infraction
            </button>
            <button
              onClick={() => setShowWarningModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              Issue Warning
            </button>
          </div>
        }
      />

      {notice && (
        <div className="mb-4 bg-amber-50 border border-amber-300 text-amber-900 px-4 py-3 rounded-lg flex items-start justify-between gap-4">
          <div className="text-sm">{notice}</div>
          <button onClick={() => setNotice(null)} className="text-amber-700 hover:text-amber-900 text-sm font-medium">Dismiss</button>
        </div>
      )}

      {!loading && bannerErrors.length > 0 && (
        <div className="space-y-2 mb-4">
          {bannerErrors.map(e => <LoadError key={e.what} what={e.what} message={e.message} onRetry={loadData} />)}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">
              {loadErrors.statuses ? '—' : employeeStatuses.filter(e => e.warningLevel === 'none').length}
            </div>
            <div className="text-sm text-gray-500">Clean Record</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600">
              {loadErrors.statuses ? '—' : employeeStatuses.filter(e => ['verbal', 'written'].includes(e.warningLevel)).length}
            </div>
            <div className="text-sm text-gray-500">Active Warnings</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600">
              {loadErrors.statuses ? '—' : employeeStatuses.filter(e => ['final', 'suspended'].includes(e.warningLevel)).length}
            </div>
            <div className="text-sm text-gray-500">Critical Status</div>
          </div>
        </Card>
        <Card className={clsx(reviewCount && reviewCount.lifetimeMaxDrafts > 0 && 'ring-2 ring-red-500')}>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">{reviewCount ? reviewCount.total : '—'}</div>
            <div className="text-sm text-gray-500">Pending Review</div>
            {reviewCount && reviewCount.total > 0 && (
              <div className="text-xs text-gray-500 mt-1">
                {reviewCount.draftWarnings} draft warning{reviewCount.draftWarnings === 1 ? '' : 's'} · {reviewCount.pendingInfractions} infraction{reviewCount.pendingInfractions === 1 ? '' : 's'}
              </div>
            )}
            {reviewCount && reviewCount.lifetimeMaxDrafts > 0 && (
              <div className="text-xs font-bold text-red-600 mt-1">{reviewCount.lifetimeMaxDrafts} lifetime max</div>
            )}
            {reviewCount && reviewCount.disputes > 0 && (
              <div className="text-xs font-bold text-red-600 mt-1">{reviewCount.disputes} disputed by employee</div>
            )}
            {reviewCount && reviewCount.staleDisputes > 0 && (
              <div className="text-xs font-bold text-amber-700 mt-1">{reviewCount.staleDisputes} dispute{reviewCount.staleDisputes === 1 ? '' : 's'} unanswered (&gt;{STALE_DISPUTE_DAYS}d)</div>
            )}
            {reviewCount && reviewCount.staleDrafts > 0 && (
              <div className="text-xs font-bold text-amber-700 mt-1">{reviewCount.staleDrafts} stale (&gt;{STALE_DRAFT_DAYS}d)</div>
            )}
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b mb-4">
        <div className="flex gap-4">
          {[
            { id: 'employees', label: 'Employee Status' },
            { id: 'pending', label: `Pending Review (${reviewCount ? reviewCount.total : '—'})` },
            { id: 'infractions', label: 'Infraction History' },
            { id: 'warnings', label: 'Warning History' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id as any)}
              className={clsx(
                'px-4 py-2 font-medium border-b-2 -mb-px',
                tab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <Card>
          {/* Employee Status Tab */}
          {tab === 'employees' && loadErrors.statuses && (
            <LoadError what="employee status" message={loadErrors.statuses} onRetry={loadData} />
          )}
          {tab === 'employees' && !loadErrors.statuses && loadErrors.drafts && (
            <div className="mb-4">
              <LoadError
                what="pending drafts — the Status column below does not show drafts awaiting review"
                message={loadErrors.drafts}
                onRetry={loadData}
              />
            </div>
          )}
          {tab === 'employees' && !loadErrors.statuses && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-500 text-sm border-b">
                    <th className="pb-3 font-medium">Employee</th>
                    <th className="pb-3 font-medium">Badge</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium text-center">30 Days</th>
                    <th className="pb-3 font-medium text-center">90 Days</th>
                    <th className="pb-3 font-medium text-center">Lifetime</th>
                    <th className="pb-3 font-medium">Last Incident</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeStatuses.map(emp => (
                    <tr key={emp.employeeId} className="border-b hover:bg-gray-50">
                      <td className="py-3 font-medium">{emp.employeeName}</td>
                      <td className="py-3 text-gray-600">{emp.badgeNumber}</td>
                      <td className="py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <WarningLevelBadge level={emp.warningLevel} />
                          {draftsByEmployee[emp.employeeId] && (
                            <button
                              type="button"
                              onClick={() => setTab('pending')}
                              title="Drafts awaiting review. Not issued; the level above does not include them."
                              className={clsx(
                                'px-2 py-0.5 rounded text-xs font-medium border hover:underline',
                                draftsByEmployee[emp.employeeId].lifetimeMax > 0
                                  ? 'bg-red-600 text-white border-red-700'
                                  : 'bg-orange-50 text-orange-700 border-orange-300'
                              )}
                            >
                              {draftsByEmployee[emp.employeeId].count} pending draft{draftsByEmployee[emp.employeeId].count === 1 ? '' : 's'}
                              {draftsByEmployee[emp.employeeId].lifetimeMax > 0 && ' · lifetime max'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-3 text-center">
                        <span className={clsx(
                          'font-medium',
                          emp.infractions30Days >= 3 ? 'text-red-600' : emp.infractions30Days > 0 ? 'text-yellow-600' : 'text-gray-400'
                        )}>
                          {emp.infractions30Days}
                        </span>
                      </td>
                      <td className="py-3 text-center">
                        <span className={clsx(
                          'font-medium',
                          emp.infractions90Days >= 7 ? 'text-red-600' : emp.infractions90Days > 0 ? 'text-yellow-600' : 'text-gray-400'
                        )}>
                          {emp.infractions90Days}
                        </span>
                      </td>
                      <td className="py-3 text-center">
                        <span className={clsx(
                          'font-medium',
                          emp.infractionsTotal >= lifetimeMax * 0.8 ? 'text-red-600' : 'text-gray-600'
                        )}>
                          {emp.infractionsTotal} / {lifetimeMax}
                        </span>
                      </td>
                      <td className="py-3 text-gray-500 text-sm">
                        {emp.lastInfractionDate ? new Date(emp.lastInfractionDate).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                  {employeeStatuses.length === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-gray-500">No employee records</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pending Review Tab */}
          {tab === 'pending' && (
            <div className="space-y-6">
              {loadErrors.reviewCount && (
                <LoadError what="the pending review count" message={loadErrors.reviewCount} onRetry={loadData} />
              )}

              {(loadErrors.disputes || disputes.length > 0) && (
                <section>
                  <h3 className="text-sm font-semibold text-red-700 uppercase tracking-wide mb-3">
                    Disputed by employee {!loadErrors.disputes && `(${disputes.length})`}
                  </h3>
                  {loadErrors.disputes ? (
                    <LoadError what="disputed records" message={loadErrors.disputes} onRetry={loadData} />
                  ) : (
                    <div className="space-y-3">
                      {disputes.map(d => <DisputeCard key={`${d.kind}-${d.id}`} dispute={d} onDone={loadData} />)}
                    </div>
                  )}
                </section>
              )}

              <section>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Draft warnings {!loadErrors.drafts && `(${draftWarnings.length})`}
                </h3>
                {loadErrors.drafts ? (
                  <LoadError what="draft warnings" message={loadErrors.drafts} onRetry={loadData} />
                ) : draftWarnings.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">No draft warnings awaiting review</div>
                ) : (
                  <div className="space-y-3">
                    {draftWarnings.map(d => <DraftWarningCard key={d.id} draft={d} onReview={() => setReviewDraft(d)} />)}
                  </div>
                )}
              </section>

              <section>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Pending infractions {!loadErrors.pending && `(${pendingInfractions.length})`}
                </h3>
              {loadErrors.pending ? (
                <LoadError what="pending infractions" message={loadErrors.pending} onRetry={loadData} />
              ) : pendingInfractions.length === 0 ? (
                <div className="text-center py-6 text-gray-500">No pending infractions to review</div>
              ) : (
                <div className="space-y-4">
                {pendingInfractions.map(inf => (
                  <div key={inf.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{inf.employeeName}</span>
                          <span className="text-gray-400">({inf.badgeNumber})</span>
                          <SeverityBadge severity={inf.severity} />
                        </div>
                        <div className="text-lg font-medium text-gray-900">{inf.infractionTypeName}</div>
                        <div className="text-sm text-gray-600 mt-1">{inf.description}</div>
                        {inf.orderNumber && (
                          <div className="text-sm text-blue-600 mt-1">Order: {inf.orderNumber}</div>
                        )}
                        <div className="text-xs text-gray-400 mt-2">
                          Submitted by {inf.submittedByName} on {new Date(inf.createdAt).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {pointsEnabled && <span className="text-red-600 font-bold">-{inf.pointsDeducted} pts</span>}
                        <button
                          onClick={() => handleApproveInfraction(inf.id)}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleRejectInfraction(inf.id)}
                          className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              )}
              </section>
            </div>
          )}

          {/* Infraction History Tab */}
          {tab === 'infractions' && loadErrors.infractions && (
            <LoadError what="infraction history" message={loadErrors.infractions} onRetry={loadData} />
          )}
          {tab === 'infractions' && !loadErrors.infractions && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-500 text-sm border-b">
                    <th className="pb-3 font-medium">Date</th>
                    <th className="pb-3 font-medium">Employee</th>
                    <th className="pb-3 font-medium">Infraction</th>
                    <th className="pb-3 font-medium">Severity</th>
                    <th className="pb-3 font-medium text-right">Points</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Acknowledgment</th>
                    <th className="pb-3 font-medium text-center">Files</th>
                    <th className="pb-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {infractions.length === 0 && (
                    <tr><td colSpan={9} className="py-8 text-center text-gray-500">No infractions recorded</td></tr>
                  )}
                  {infractions.map(inf => (
                    <tr key={inf.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 text-sm">{new Date(inf.incidentDate).toLocaleDateString()}</td>
                      <td className="py-3 font-medium">{inf.employeeName}</td>
                      <td className="py-3">
                        <div className={clsx(inf.status === 'reversed' && 'line-through text-gray-400')}>{inf.infractionTypeName}</div>
                        {inf.orderNumber && <div className="text-xs text-blue-600">Order: {inf.orderNumber}</div>}
                        {inf.status === 'reversed' && (
                          <div className="mt-1 text-xs text-gray-700 bg-gray-100 border border-gray-300 rounded px-2 py-1">
                            <span className="font-semibold">Reversed</span>
                            {inf.reversedAt ? ` ${new Date(inf.reversedAt).toLocaleDateString()}` : ''}
                            {inf.reversedByName ? ` by ${inf.reversedByName}` : ''}: {inf.reversalReason}
                          </div>
                        )}
                      </td>
                      <td className="py-3"><SeverityBadge severity={inf.severity} /></td>
                      {/* points_deducted is what was actually taken; 0 since discipline stopped touching rewards */}
                      <td className="py-3 text-right text-red-600 font-medium">{inf.pointsDeducted > 0 ? `-${inf.pointsDeducted}` : <span className="text-gray-400">—</span>}</td>
                      <td className="py-3">
                        <span className={clsx(
                          'px-2 py-0.5 rounded text-xs',
                          inf.status === 'approved' ? 'bg-green-100 text-green-700' :
                          inf.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          inf.status === 'reversed' ? 'bg-white text-gray-700 border border-gray-500' :
                          'bg-yellow-100 text-yellow-700'
                        )}>
                          {inf.status}
                        </span>
                      </td>
                      <td className="py-3">{inf.status === 'approved' ? <AckStatus record={inf} /> : <span className="text-gray-400 text-xs">—</span>}</td>
                      <td className="py-3 text-center">
                        <AttachmentsButton
                          count={inf.attachmentCount}
                          onClick={() => setAttachmentsFor({ infractionId: inf.id, title: `${inf.employeeName} — ${inf.infractionTypeName}` })}
                        />
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setNoticeFor({ doc: infractionDoc(inf), justIssued: false })}
                            className="px-2 py-1 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-50 whitespace-nowrap"
                          >
                            Print record
                          </button>
                        {inf.status === 'approved' && (
                          <>
                            {inf.acknowledgementMethod !== 'acknowledged_in_pwa' && (
                              <button
                                onClick={() => setAckFor({ kind: 'infraction', id: inf.id, title: `${inf.employeeName} — ${inf.infractionTypeName}`, current: inf.acknowledgementMethod })}
                                className="px-2 py-1 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-50 whitespace-nowrap"
                              >
                                Record ack
                              </button>
                            )}
                            <button
                              onClick={() => setReverseFor(inf)}
                              className="px-2 py-1 text-xs border border-gray-300 text-gray-500 rounded hover:bg-gray-50 whitespace-nowrap"
                            >
                              Reverse
                            </button>
                          </>
                        )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Warning History Tab */}
          {tab === 'warnings' && loadErrors.warnings && (
            <LoadError what="warning history" message={loadErrors.warnings} onRetry={loadData} />
          )}
          {tab === 'warnings' && !loadErrors.warnings && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-500 text-sm border-b">
                    <th className="pb-3 font-medium">Date</th>
                    <th className="pb-3 font-medium">Employee</th>
                    <th className="pb-3 font-medium">Type</th>
                    <th className="pb-3 font-medium">Reason</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Expires</th>
                    <th className="pb-3 font-medium">Acknowledgment</th>
                    <th className="pb-3 font-medium text-center">Files</th>
                    <th className="pb-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {warnings.length === 0 && (
                    <tr><td colSpan={9} className="py-8 text-center text-gray-500">No warnings recorded</td></tr>
                  )}
                  {warnings.map(warn => (
                    <tr key={warn.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 text-sm">{new Date(warn.status === 'draft' ? warn.createdAt : warn.issuedAt).toLocaleDateString()}</td>
                      <td className="py-3 font-medium">{warn.employeeName}</td>
                      <td className="py-3"><WarningLevelBadge level={warn.warningType} /></td>
                      <td className="py-3 text-sm text-gray-600 max-w-xs">
                        <div className={clsx('truncate', warn.status === 'rescinded' && 'line-through text-gray-400')}>{warn.reason}</div>
                        {warn.status === 'rescinded' && (
                          <div className="mt-1 text-xs text-gray-700 bg-gray-100 border border-gray-300 rounded px-2 py-1 whitespace-normal">
                            <span className="font-semibold">Rescinded</span>
                            {warn.rescindedAt ? ` ${new Date(warn.rescindedAt).toLocaleDateString()}` : ''}
                            {warn.rescindedByName ? ` by ${warn.rescindedByName}` : ''}: {warn.rescissionReason}
                          </div>
                        )}
                      </td>
                      <td className="py-3">
                        <span className={clsx(
                          'px-2 py-0.5 rounded text-xs',
                          warn.status === 'acknowledged' ? 'bg-green-100 text-green-700' :
                          warn.status === 'pending_acknowledgement' ? 'bg-yellow-100 text-yellow-700' :
                          warn.status === 'draft' ? 'bg-white text-gray-600 border border-gray-300' :
                          warn.status === 'refused' ? 'bg-red-100 text-red-700' :
                          warn.status === 'rescinded' ? 'bg-white text-gray-700 border border-gray-500' :
                          'bg-gray-100 text-gray-700'
                        )}>
                          {warn.status === 'draft' ? 'draft — not issued' : warn.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-3 text-sm text-gray-500">
                        {warn.status === 'draft' ? '—' : warn.expiresAt ? new Date(warn.expiresAt).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="py-3">{['pending_acknowledgement', 'acknowledged', 'refused'].includes(warn.status) ? <AckStatus record={warn} /> : <span className="text-gray-400 text-xs">—</span>}</td>
                      <td className="py-3 text-center">
                        <AttachmentsButton
                          count={warn.attachmentCount}
                          onClick={() => setAttachmentsFor({ warningId: warn.id, title: `${warn.employeeName} — ${warningTypeLabel(warn.warningType)}` })}
                        />
                      </td>
                      <td className="py-3 text-right">
                        {/* Not on drafts: a draft isn't a notice. Other refusals come from the API. */}
                        {!['draft', 'rescinded'].includes(warn.status) && (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => setNoticeFor({ doc: warningDoc(warn), justIssued: false })}
                              className="px-2 py-1 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-50 whitespace-nowrap"
                            >
                              Print notice
                            </button>
                            {['pending_acknowledgement', 'acknowledged', 'refused'].includes(warn.status) && warn.acknowledgementMethod !== 'acknowledged_in_pwa' && (
                              <button
                                onClick={() => setAckFor({ kind: 'warning', id: warn.id, title: `${warn.employeeName} — ${warningTypeLabel(warn.warningType)}`, current: warn.acknowledgementMethod })}
                                className="px-2 py-1 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-50 whitespace-nowrap"
                              >
                                Record ack
                              </button>
                            )}
                            <button
                              onClick={() => setRescindFor(warn)}
                              className="px-2 py-1 text-xs border border-gray-300 text-gray-500 rounded hover:bg-gray-50 whitespace-nowrap"
                            >
                              Rescind
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Modals */}
      <IssueInfractionModal
        isOpen={showInfractionModal}
        onClose={() => setShowInfractionModal(false)}
        subjects={subjects}
        employees={employees}
        infractionTypes={infractionTypes}
        pointsEnabled={pointsEnabled}
        onSubmit={handleIssueInfraction}
      />
      {reviewDraft && (
        <IssueDraftModal
          draft={reviewDraft}
          employees={employees}
          settings={settings}
          onClose={() => setReviewDraft(null)}
          onDone={(n, issued) => {
            setReviewDraft(null);
            if (n) setNotice(n);
            loadData();
            if (issued?.id) setNoticeFor({ doc: warningDoc(issued), justIssued: true });
          }}
        />
      )}
      <IssueWarningModal
        isOpen={showWarningModal}
        onClose={() => { setShowWarningModal(false); setWarningPrefillEmployeeId(''); if (window.location.search) window.history.replaceState({}, '', window.location.pathname); }}
        subjects={subjects}
        employees={employees}
        settings={settings}
        onSubmit={handleIssueWarning}
        prefillEmployeeId={warningPrefillEmployeeId}
      />
      {reverseFor && (
        <ReverseInfractionModal
          infraction={reverseFor}
          onClose={() => setReverseFor(null)}
          onDone={() => { setReverseFor(null); loadData(); }}
        />
      )}
      {rescindFor && (
        <RescindWarningModal
          warning={rescindFor}
          onClose={() => setRescindFor(null)}
          onDone={() => { setRescindFor(null); loadData(); }}
        />
      )}
      {noticeFor && (
        <PrintNoticeModal
          doc={noticeFor.doc}
          justIssued={noticeFor.justIssued}
          onClose={() => setNoticeFor(null)}
          onAttach={() => {
            const d = noticeFor.doc;
            setNoticeFor(null);
            setAttachmentsFor({ ...(d.kind === 'warning' ? { warningId: d.id } : { infractionId: d.id }), title: `${d.employeeName} — ${d.label}` });
          }}
          onRecordAck={() => {
            const d = noticeFor.doc;
            setNoticeFor(null);
            setAckFor({ kind: d.kind, id: d.id, title: `${d.employeeName} — ${d.label}`, current: d.acknowledgementMethod });
          }}
        />
      )}
      {ackFor && (
        <RecordAcknowledgementModal
          target={ackFor}
          onClose={() => setAckFor(null)}
          onDone={() => { setAckFor(null); loadData(); }}
        />
      )}
      {attachmentsFor && (
        <AttachmentsModal
          target={attachmentsFor}
          onClose={() => setAttachmentsFor(null)}
          onChanged={loadData}
        />
      )}
    </>
  );
}
